import { useForm, conform } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import {
	json,
	unstable_parseMultipartFormData as parseMultipartFormData,
	unstable_createMemoryUploadHandler as createMemoryUploadHandler,
	type DataFunctionArgs,
} from '@remix-run/node'
import {
	Form,
	Link,
	useActionData,
	useFetchers,
	useLoaderData,
	useNavigate,
	useNavigation,
	useOutletContext,
	useSubmit,
} from '@remix-run/react'
import { useEffect, useRef, useState } from 'react'
import { ClientOnly } from 'remix-utils/client-only'
import { z } from 'zod'
import { CheckboxField, ErrorListAlert } from '#app/components/forms.tsx'
import { Image } from '#app/components/image.client.tsx'
import { InformationBox } from '#app/components/info-box.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import {
	Tabs,
	TabsList,
	TabsContent,
	TabsTrigger,
} from '#app/components/tabs.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { ConfirmableButton } from '#app/components/ui/confirmable-button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { useLocalStorageState } from '#app/hooks/use-local-stored-state.tsx'
import { CreditCost } from '#app/services/costs.ts'
import { requireUserId } from '#app/utils/auth.server.ts'
import {
	Queries,
	invalidateClientCache,
	useCachedFetcher,
} from '#app/utils/client-side-caching/store.ts'
import {
	createTransactionTypeAIGeneration,
	getCurrentCredits,
} from '#app/utils/credits.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import {
	generateImageWithFiles,
	generateImageWithUserUrl,
	urlToBuffer,
	urlToDataUri,
} from '#app/utils/generate-image.server.ts'
import * as gtag from '#app/utils/gtags.client.tsx'
import { ensurePrimary } from '#app/utils/litefs.server.ts'
import { cn } from '#app/utils/misc.tsx'
import { uploadAndGetUrl, uploadToS3 } from '#app/utils/s3.server.ts'
import { makeThumbnail } from '#app/utils/thumb.server.ts'
import {
	ImageChooser,
	ImageFieldsetSchema,
	MAX_MB_SIZE,
	MAX_UPLOAD_SIZE,
} from './components/image-chooser.tsx'
import { ResultImage } from './components/result-image.tsx'
import { type loader as userImagesLoader } from './user-images.ts'

const fakeImageGet = async (file: string) => {
	return new Promise<string | null>(resolve => {
		setTimeout(() => {
			resolve(file + '?random=' + Math.random())
		}, 500)
	})
}

const deleteUserImageActionIntent = 'delete-image'

const UploadFilesSchema = z
	.object({
		id: z.string().optional(),
		user_image: ImageFieldsetSchema.optional().nullable(),
		swap_image: ImageFieldsetSchema,
		user_image_url: z.string().optional(),
		result_image: z.string().optional(),
		save_user_image: z.boolean().optional(),
	})
	.refine(
		data => Boolean(data.user_image_url) || Boolean(data.user_image?.file),
		'Please upload or select a photo of your face',
	)

async function saveUserImage(userId: string, userImage: File) {
	let userImageBuffer = Buffer.from(await userImage.arrayBuffer())
	let fileName = await uploadToS3(userId, userImageBuffer)
	let thumbFileName = await uploadToS3(
		'thumb_' + userId,
		await makeThumbnail(userImageBuffer),
	)
	return await prisma.userTargetImage.create({
		data: {
			user: { connect: { id: userId } },
			name: fileName,
			thumbnailName: thumbFileName,
		},
	})
}

export async function loader({ request }: DataFunctionArgs) {
	let url = new URL(request.url)

	let imageUrl = url.searchParams.get('img')
	if (imageUrl) {
		try {
			let dataUri = await urlToDataUri(imageUrl)
			return json({ swapImage: dataUri })
		} catch (e) {
			return json({ swapImage: null })
		}
	}

	return json({ swapImage: null })
}

export async function action({ request }: DataFunctionArgs) {
	let userId = await requireUserId(request)
	let currentCredits = await getCurrentCredits(userId)

	if (
		currentCredits < CreditCost.UseOfSwap &&
		process.env.NODE_ENV !== 'development'
	) {
		return json({
			status: 'error_credits',
			submission: null,
			result: null,
			message: `You don't have enough credits to generate more images`,
		})
	}

	let formData
	try {
		formData = await parseMultipartFormData(
			request,
			createMemoryUploadHandler({ maxPartSize: MAX_UPLOAD_SIZE + 1024 * 1024 }),
		)
	} catch (e) {
		return json({
			status: 'error',
			submission: null,
			result: null,
			message: `There was an error uploading the images, please try again. If the error persists use another image of less size.`,
		})
	}

	const submission = await parse(formData, {
		schema: UploadFilesSchema.transform(async (data, ctx) => {
			if (
				!data.user_image_url &&
				(!data.user_image?.file || data.user_image.file.size <= 0)
			) {
				ctx.addIssue({
					path: ['user_image', 'user_image_url'],
					code: z.ZodIssueCode.custom,
					message: 'Please upload or select a photo of your face',
				})
				return z.NEVER
			}
			return data
		}),
		async: true,
	})

	if (submission.intent !== 'submit') {
		return json({
			status: 'idle',
			submission,
			result: null,
			message: '',
		} as const)
	}

	if (!submission.value) {
		return json(
			{ status: 'error', submission, result: null, message: '' } as const,
			{
				status: 400,
			},
		)
	}

	let userImage = submission.value.user_image?.file
	let swapImage = submission.value.swap_image.file
	let userImageUrl = submission.value.user_image_url

	if (!swapImage) {
		return json(
			{
				status: 'error',
				submission,
				result: null,
				message: 'The image to put your face in is required',
			} as const,
			{
				status: 400,
			},
		)
	}

	let result: string | null
	if (process.env.GENERATE_RANDOM_IMAGE) {
		// result = await fakeImageGet('https://picsum.photos/seed/picsum/200/300')
		result = await fakeImageGet(
			'http://localhost:3000/replicate-prediction-xd2gnibbrj62a3fwd3s5fa2qgq.jpg',
		)
	} else {
		if (userImageUrl) {
			result = await generateImageWithUserUrl(userImageUrl, swapImage)
		} else {
			userImage = userImage as File
			result = await generateImageWithFiles(userImage, swapImage)
		}
	}

	if (!result || typeof result !== 'string') {
		return json(
			{
				status: 'error_image',
				submission,
				result: null,
				message:
					'There was an error generating the image, please try again. If the error persists use another image ' +
					'of less size. Please note that if you are trying to put your face in a photo of an animal or something that is not a human face ' +
					'it can fail to generate the image.',
			} as const,
			{
				status: 400,
			},
		)
	}

	let saveImage = submission.value.save_user_image
	if (saveImage && userImage) {
		await saveUserImage(userId, userImage)
	}

	await ensurePrimary()
	let realUrl = await uploadAndGetUrl(
		`${userId}-swap`,
		await urlToBuffer(result),
	)
	await prisma.generatedImage.create({
		select: { id: true, url: true },
		data: {
			url: realUrl,
			owner: { connect: { id: userId } },
		},
	})
	await createTransactionTypeAIGeneration(userId)

	return json({ status: 'success', result, submission, message: '' } as const)
}

export function UploadFiles() {
	const response = useActionData<typeof action>()
	const outletContext = useOutletContext<{ credits: number }>()
	const resultImage = response?.result
	const errorMessage = response?.message
	const navigate = useNavigate()

	const data = useLoaderData<typeof loader>()

	const [image, setImage] = useState<string>(resultImage ?? '')
	useEffect(() => {
		setImage(resultImage ?? '')
		invalidateClientCache('userSavedImages')
	}, [resultImage])

	const navigation = useNavigation()
	const isPending = navigation.state === 'submitting'

	const [form, fields] = useForm({
		id: 'images-upload',
		constraint: getFieldsetConstraint(UploadFilesSchema),
		lastSubmission: response?.submission || undefined,
		onValidate({ formData }) {
			return parse(formData, { schema: UploadFilesSchema })
		},
		defaultValue: {
			swap_image: {},
			save_user_image: false,
		},
	})

	function getErrorList() {
		if (errorMessage) {
			return [errorMessage]
		}
		if (form.errors.length > 0) {
			return form.errors
		}

		return []
	}
	const creditsInsufficient = response?.status === 'error_credits'
	const currentCredits = outletContext?.credits ?? 0

	return (
		<div className="flex flex-col gap-8">
			<ClientOnly>{() => <TipsBox />}</ClientOnly>
			{creditsInsufficient && (
				<Button asChild className="bg-app-gradient">
					<Link to="/plans">Buy more credits</Link>
				</Button>
			)}
			<Form
				{...form.props}
				method="post"
				className="flex w-full flex-col justify-between gap-4 md:flex-row"
				encType="multipart/form-data"
				onSubmit={() => {
					gtag.event({
						action: 'submit photos',
						category: 'upload',
						label: 'upload',
					})
				}}
			>
				<div className="flex-[1]">
					<UserImage fields={fields} onChange={() => setImage('')} />
				</div>
				<div className="flex flex-col items-center justify-center gap-6">
					<Icon
						className="aspect-square h-auto w-[35%] rotate-180 md:mt-0 md:rotate-90"
						name="merge"
					/>
					{currentCredits > 0 ? (
						<Button disabled={isPending} type="submit" onClick={scrollToResult}>
							Merge my face{' '}
							<span className="text-xs opacity-60">&nbsp;~5s</span>
						</Button>
					) : (
						<>
							<Button className="bg-app-gradient" asChild size="lg">
								<Link to="/plans">Buy more credits</Link>
							</Button>
							<span className="opacity-60">No credits left</span>
						</>
					)}
					{!!resultImage && (
						<Button type="button" onClick={() => navigate(0)}>
							Do another one
						</Button>
					)}
				</div>
				<div className="mt-12 flex-[1] md:mt-0">
					<ImageChooser
						config={fields.swap_image}
						label="A photo to put your face in"
						onChange={() => setImage('')}
						swapImageOnUrl={data.swapImage}
					/>
				</div>
			</Form>
			<div className="flex w-full">
				<span className="hidden flex-1 md:inline" />
				<div className="flex-1">
					{getErrorList().length > 0 && (
						<div className="mb-2">
							<ErrorListAlert
								elementClassName="text-md"
								id="generation-error"
								errors={getErrorList()}
							>
								<Button
									size="sm"
									type="submit"
									variant="ghost"
									form="images-upload"
								>
									Try again
								</Button>
							</ErrorListAlert>
						</div>
					)}
					<ResultImage src={image ?? ''} loading={isPending} />
				</div>
				<span className="hidden flex-1 md:inline" />
			</div>
			<Spacer size="xs" />
		</div>
	)
}

function scrollToResult() {
	setTimeout(() => {
		let resultEl = document.getElementById('result-image')
		if (resultEl) {
			resultEl.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			})
		}
	}, 200)
}

function CustomTabContent(props: { children: React.ReactNode }) {
	return (
		<div className="rounded-md border-[1px] px-6 py-3">{props.children}</div>
	)
}

function UserImage(props: any) {
	return (
		<div>
			<Label htmlFor={props.fields.user_image.id}>A photo of your face</Label>
			<div className="h-2" />
			<Tabs defaultValue="upload">
				<TabsList>
					<TabsTrigger value="upload">Upload a picture</TabsTrigger>
					<TabsTrigger value="select">Select from previously used</TabsTrigger>
				</TabsList>
				<TabsContent value="upload">
					<CustomTabContent>
						<ImageChooser
							config={props.fields.user_image}
							onChange={() => props.onChange()}
						/>
						<CheckboxField
							labelProps={{
								htmlFor: props.fields.save_user_image.id,
								children: 'Save this photo for future use',
							}}
							buttonProps={conform.input(props.fields.save_user_image, {
								type: 'checkbox',
							})}
							errors={props.fields.save_user_image.errors}
						/>
					</CustomTabContent>
				</TabsContent>
				<TabsContent value="select">
					<CustomTabContent>
						<PreviousUserImages fields={props.fields} />
					</CustomTabContent>
				</TabsContent>
			</Tabs>
		</div>
	)
}

function fakeImage() {
	return {
		id: 'fake-' + Math.random(),
		name: 'fake-' + Math.random(),
		thumbnailUrl: '',
		url: '',
		thumbnailName: 'fake-' + Math.random(),
	}
}

function useDeletedImages() {
	const ref = useRef(new Set())

	let fetchers = useFetchers()
	let deletingFetchers = fetchers
		.filter(f => !!f.formData)
		.filter(f => f.formData?.get('intent') === deleteUserImageActionIntent)
		.map(f => f.formData?.get('imageId'))
		.filter(Boolean) as string[]

	deletingFetchers.forEach((imageId: string) => {
		if (!ref.current.has(imageId)) {
			ref.current.add(imageId)
		}
	})

	return ref.current
}

function PreviousUserImages(props: any) {
	const [selected, setSelected] = useState<string | null>(null)
	const userImagesFetcher = useCachedFetcher<typeof userImagesLoader>(
		Queries.userSavedImages.key(),
		Queries.userSavedImages.url(),
	)
	let loading = userImagesFetcher.data == null
	let images = userImagesFetcher.data?.images ?? []

	let deletedImages = useDeletedImages()
	images = images.filter(image => !deletedImages.has(image.id))

	if (loading) {
		images = Array.from({ length: 6 }, () => fakeImage())
	}

	function handleSelectImage(image: (typeof images)[number]) {
		if (selected === image.name) {
			setSelected(null)
		} else {
			setSelected(image.url ?? '')
		}
	}

	function isSelected(image: (typeof images)[number]) {
		return selected === image.url
	}
	// what class is used to hide the scroll ui with tailwind?
	// overflow-y-hidden
	let height = 'h-[34rem]'

	return (
		<div
			className={cn(
				height,
				'custom-scrollbar overflow-x-hidden overflow-y-scroll',
			)}
		>
			<input type="hidden" name="user_image_url" value={selected ?? ''} />
			{!loading && images.length === 0 && (
				<span className="text-sm opacity-60">
					You haven't saved any pictures for future use yet.
				</span>
			)}
			{!loading && images.length > 0 && (
				<span className="pb-1 opacity-30">
					<Icon name="info">Just click any image to select it</Icon>
				</span>
			)}
			<div
				className={cn('mr-[-4px] grid grid-cols-2 gap-2 pr-2 md:grid-cols-3')}
			>
				<ClientOnly>
					{() =>
						images.map(image => (
							<PreviousImage
								imageId={image.id}
								src={image.thumbnailUrl ?? ''}
								key={image.name}
								isSelected={isSelected(image)}
								onSelect={() => handleSelectImage(image)}
							/>
						))
					}
				</ClientOnly>
			</div>
		</div>
	)
}

function PreviousImage(props: {
	src: string
	imageId: string
	isSelected: boolean
	onSelect: () => void
}) {
	const submit = useSubmit()

	return (
		<div
			tabIndex={0}
			role="radio"
			className="relative h-fit w-full hover:cursor-pointer"
			aria-checked={props.isSelected}
			onClick={props.onSelect}
			onKeyDown={e => {
				if (e.key === 'Enter') {
					props.onSelect()
				}
			}}
		>
			{props.isSelected && (
				<span className="absolute bottom-0 left-0 right-0 flex w-full items-center justify-center bg-background/70 p-2 text-sm">
					Use this one
				</span>
			)}
			<Image
				className={cn(
					'min-w-10 aspect-square h-auto w-full cursor-pointer rounded-sm border-[1px] border-solid border-primary/20 object-cover',
					{ 'border-4 border-foreground': props.isSelected },
				)}
				src={props.src}
				alt="face that was used previously to create a face swap"
			/>
			<div className="absolute left-0 top-0 flex h-full w-full opacity-0 transition duration-100 ease-in-out hover:opacity-100">
				<div className="flex h-full w-full flex-col items-end justify-end px-1 pb-1">
					<div className="flex h-full items-start justify-end pr-1 pt-2">
						<ConfirmableButton
							size="sm"
							variant="destructive"
							onConfirm={() => {
								submit(
									{
										intent: deleteUserImageActionIntent,
										imageId: props.imageId,
									},
									{
										fetcherKey: `userSavedImage:${props.imageId}`,
										method: 'post',
										navigate: false,
										action: '/me/action/delete-user-image',
									},
								)
							}}
							confirmText="Are you sure?"
						>
							<Icon name="trash" />
						</ConfirmableButton>
					</div>
				</div>
			</div>
		</div>
	)
}

export function TipsBox() {
	const [show, setShow] = useLocalStorageState('show-tips-box', true)

	if (!show) {
		return null
	}

	return (
		<InformationBox>
			<div>
				<span>Tips to get better results</span>
				<ul className="list-inside list-disc">
					<li>
						Make sure the photo of your face is very clear, caps and sunglasses
						are accepted but can compromise the final quality of the image.
					</li>
					<li>Make sure the photo of your face is at least 480p quality.</li>
					<li>
						None of the picture can be longer than {MAX_MB_SIZE}MB in size.
					</li>
				</ul>
			</div>
			<div className="flex flex-row justify-end">
				<Button
					onClick={() => setShow(false)}
					variant="link"
					className="self-end"
				>
					Don't show anymore
				</Button>
			</div>
		</InformationBox>
	)
}
